import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { AuthContext, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QHubBuddyLogo } from "@/components/QHubBranding";
import { ArrowLeft, Brain, Sparkles, Zap, BarChart3 } from "lucide-react";

const DIMENSION_LABELS = {
  comprension: "Comprensión",
  inferencia: "Inferencia",
  analisis: "Análisis",
  resolucion: "Resolución",
  argumentacion: "Argumentación",
  transferencia: "Transferencia",
  persistencia: "Persistencia",
  metacognicion: "Metacognición",
};
const LEVEL_COLORS = {
  "Alto":          "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Medio":         "bg-amber-100 text-amber-700 border-amber-200",
  "En desarrollo": "bg-orange-100 text-orange-700 border-orange-200",
  "Por explorar":  "bg-gray-100 text-gray-500 border-gray-200",
};

const DimensionBar = ({ dim, score, level }) => {
  const pct = Math.round(Math.min(1, Math.max(0, Number(score || 0))) * 100);
  const color = level === "Alto" ? "bg-emerald-500"
    : level === "Medio" ? "bg-amber-500"
    : level === "En desarrollo" ? "bg-orange-400"
    : "bg-gray-300";
  return (
    <div className="space-y-1.5" data-testid={`dimbar-${dim}`}>
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-gray-700">{DIMENSION_LABELS[dim] || dim}</span>
        <Badge className={`border ${LEVEL_COLORS[level] || ""}`}>{level || "-"}</Badge>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[11px] text-gray-400">{pct}%</p>
    </div>
  );
};

const RetoAvanzoProfile = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/reto-avanzo/me/profile`, { withCredentials: true });
        setData(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-mesh flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data?.completed) {
    return (
      <div className="min-h-screen bg-mesh flex items-center justify-center p-6">
        <div className="max-w-md bg-white border border-gray-100 rounded-2xl p-6 text-center space-y-3">
          <Brain className="w-10 h-10 text-sky-500 mx-auto" />
          <p className="text-gray-700">Aún no has completado tu diagnóstico inicial.</p>
          <Button onClick={() => navigate("/reto-avanzo/diagnostico")} className="bg-sky-600 hover:bg-sky-700">Iniciar diagnóstico</Button>
        </div>
      </div>
    );
  }

  const base = data.diagnostic_profile || {};
  const live = data.live_profile || {};
  const hasLive = live.has_live_data && live.interactions > 0;
  const profile = hasLive ? live : base;

  return (
    <div className="min-h-screen bg-mesh">
      <header className="bg-white/80 backdrop-blur border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/tutor/reto-avanzo")} data-testid="profile-back">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <QHubBuddyLogo size="default" />
          </div>
          <Badge className="bg-sky-50 text-sky-700">Mi Perfil Cognitivo</Badge>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div className="bg-gradient-to-br from-sky-500 to-indigo-600 text-white rounded-3xl p-8 shadow-xl">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-6 h-6" />
                <p className="uppercase tracking-wider text-xs opacity-90">{hasLive ? "Perfil en evolución" : "Perfil inicial"}</p>
              </div>
              <h1 className="text-4xl font-bold" data-testid="profile-label">{profile.profile_label || "—"}</h1>
              <p className="text-white/90 mt-2 max-w-2xl">{profile.summary}</p>
            </div>
            <div className="text-right text-white/90 text-sm space-y-1">
              <p>Interacciones: <span className="font-semibold">{live.interactions || 0}</span></p>
              {!hasLive && <p className="text-white/70 text-xs">Tu perfil evolucionará a medida que entrenes</p>}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-sky-500" />
              <h2 className="font-semibold text-gray-900">Dimensiones cognitivas</h2>
            </div>
            <div className="space-y-4">
              {Object.entries(profile.dimension_scores || {}).map(([d, s]) => (
                <DimensionBar key={d} dim={d} score={s} level={(profile.dimension_levels || {})[d]} />
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-5 h-5 text-amber-500" />
                <h2 className="font-semibold text-gray-900">Comparación con tu diagnóstico inicial</h2>
              </div>
              {hasLive ? (
                <div className="space-y-2 text-sm">
                  {Object.keys(base.dimension_scores || {}).map(d => {
                    const oldS = Number((base.dimension_scores || {})[d] || 0);
                    const newS = Number((live.dimension_scores || {})[d] || 0);
                    const delta = newS - oldS;
                    return (
                      <div key={d} className="flex items-center justify-between border-b border-gray-50 pb-1">
                        <span className="text-gray-700">{DIMENSION_LABELS[d] || d}</span>
                        <span className={`text-xs font-medium ${delta > 0.05 ? "text-emerald-600" : delta < -0.05 ? "text-red-600" : "text-gray-400"}`}>
                          {delta > 0 ? "↑" : delta < 0 ? "↓" : "="} {Math.abs(delta * 100).toFixed(0)}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Aún no tienes interacciones suficientes. Conversa con Reto Avanzo para que tu perfil evolucione.</p>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <p className="text-xs font-semibold text-gray-700 mb-1 uppercase">Próximo enfoque sugerido</p>
              <p className="text-sm text-gray-600">
                Trabaja especialmente en las dimensiones marcadas como{" "}
                <span className="font-medium text-orange-600">«En desarrollo»</span> o{" "}
                <span className="font-medium text-gray-500">«Por explorar»</span>.
              </p>
              <Button className="w-full mt-3 bg-sky-600 hover:bg-sky-700" onClick={() => navigate("/tutor/reto-avanzo")} data-testid="back-to-tutor">
                Continuar entrenamiento
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RetoAvanzoProfile;
